import { Box, Button, Grid, Typography } from "@mui/material";
import { FC, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { Content } from "@/components/content/Content";
import WithSave from "@/controller/WithSave";
import { InfoTypeInput, InfoTypeResponseDto } from "@/gen/api/securefile";
import { OdResourcePageProps } from "@/types/resource";
import InfoTypeEditor from "@/components/securefiles/InfoTypeEditor";
import { RecPatch } from "@/dataProvider/tools";
import Edit from "@/controller/Edit";
import { useDeleteWithConfirmController, useEditContext } from "ra-core";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import Confirm from "@/components/widgets/Confirm";
import usePermitGuard from "@/authProvider/usePermitGuard";
import { MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES } from "@/constants";
import { CollectEditsProtocol } from "@/utils/zustand-form/ext/collectEdits";
import { FormNode, useCreateZTreeStore, ZTreeStoreProvider } from "@/utils/zustand-form";
import { useStore } from "zustand";




const EditInfoType: FC<OdResourcePageProps&RecPatch> = (props) => {
  const { id, resource } = props
  const handlers = useDefaultSavedHandlers("Edit Info Type")
  usePermitGuard([[MANAGE_FILE_CLASSIFICATION_AND_PROTECTION_RULES]])
  return (
    <Edit resource={resource} basePath="" id={id} mutationMode="pessimistic" {...handlers}>
      <EditInfoTypeBody {...props} />
    </Edit>
  )
}

const EditInfoTypeBody: FC<OdResourcePageProps&RecPatch> = ({
  id, options, resource,
}) => {
  const { record, refetch } = useEditContext<InfoTypeResponseDto&RecPatch>()
  const dispatch = useDispatch()
  const zstore = useCreateZTreeStore()

  const handleSave = (save: (record:any,callbacks?:any)=>Promise<void>) => async () => {
    try {
      // Use CollectEditsProtocol to collect data from zustand store
      const payload = await CollectEditsProtocol.invoke<InfoTypeInput>(
        (protocol) => zstore.getState().invokeNodeProtocol('root', protocol) as any
      );
      (payload as any).id = id;

      await save(payload)
      refetch?.();
    } catch (error) {
      return;
    }
  };
  const dirty = useStore(zstore, state => {
    let hasChanges = Object.values(state.getNode('root')?.state.touched ?? {}).some(x => x)
    if (hasChanges) { return true }
    state.traverseDescendants('root', (next) => {
      let childNode : FormNode|null = null 
      while (!hasChanges && (childNode = next())) {
        hasChanges = Object.values(childNode.state.touched).some(x => x)
      }
    })
    return hasChanges
  })

  const delHandlers = useDefaultSavedHandlers('Delete File Info Type')
  delHandlers.onSuccess.merge(useCallback(() => { dispatch(push(`/${resource}`)) }, [dispatch, resource]))
  const {
    open: deleteDialogOpen,
    loading,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
  } = useDeleteWithConfirmController({
    resource,
    record: { id },
    mutationMode: 'pessimistic',
    ...delHandlers,
  })

  return (
    <Content
      pageName="Edit File Info Type"
      resourceName="Edit"
      breadcrumbs={[...(options?.breadcrumbs ?? []),
        { label: 'File Info Types', path: `/${resource}` }
      ]}
      mainActions={
        <>
          {!record?.isBuiltIn &&
            <Button sx={{ mr: theme => `${theme.spacing(4)} !important`}}
              color="error"
              variant="outlined"
              onClick={handleDialogOpen}
            >
              Delete
            </Button>
          }
          <WithSave 
            render={({save, saving}) =>
              <Button color="primary" variant="contained"
                disabled={saving || !dirty}
                onClick={handleSave(save)}
              >
                Save
              </Button>}
          />
          <Button color="primary" variant="outlined"
            component={RouterLink}
            to={`/${resource}`}
          >
            Cancel
          </Button>
        </>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <ZTreeStoreProvider value={zstore}>
            <InfoTypeEditor title="Edit File Info Type" />
          </ZTreeStoreProvider>
        </Grid>
      </Grid>
      <Confirm
        isOpen={deleteDialogOpen}
        title={"Delete File Info Type"}
        loading={loading}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1 }}
              >
                Please confirm whether to proceed with the deletion of this
                {(record?.name) ? ` "${record?.name}" ` : " "}
                File Info Type.
              </Typography>
            </Box>
          </Box>
        )}
      />
    </Content>
  )
}

export default EditInfoType;

